import React from 'react';
import { Profile } from '../types';
import './AuthButton.css';

interface AuthButtonProps {
  isAuthenticated: boolean;
  user: Profile | null;
  onClick: () => void;
  className?: string;
}

/**
 * Generate a consistent color for user avatar based on user ID
 */
const generateAvatarColor = (userId: string): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
    '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2',
    '#F8B739', '#52B788', '#E76F51', '#2A9D8F'
  ];
  
  // Simple hash function to get consistent color
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

/**
 * Extract initials from user name
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * AuthButton Component
 * Displays login button for guests or user avatar/name for authenticated users
 */
export const AuthButton: React.FC<AuthButtonProps> = ({
  isAuthenticated,
  user,
  onClick,
  className = ''
}) => {
  if (!isAuthenticated || !user) {
    // Not logged in - show login button
    return (
      <button
        className={`auth-button auth-button-guest ${className}`}
        onClick={onClick}
        aria-label="Open authentication menu"
        type="button"
      >
        <span className="auth-button-icon" aria-hidden="true">👤</span>
        <span className="auth-button-text">Login</span>
      </button>
    );
  }

  // Logged in - show user avatar and name
  const initials = getInitials(user.name);
  const avatarColor = generateAvatarColor(user.id);

  return (
    <button
      className={`auth-button auth-button-authenticated ${className}`}
      onClick={onClick}
      aria-label={`User menu for ${user.name}`}
      type="button"
    >
      <div
        className="auth-button-avatar"
        style={{ backgroundColor: avatarColor }}
        aria-hidden="true"
      >
        {initials}
      </div>
      <span className="auth-button-name">{user.name}</span>
      <span className="auth-button-dropdown-icon" aria-hidden="true">▼</span>
    </button>
  );
};

export default AuthButton;
